import React from 'react';
import { Link } from 'react-router-dom';
import { Github, Linkedin, Mail, ExternalLink, Instagram } from 'lucide-react';
import { useTranslation } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const socialLinks = [
    { icon: Github, href: 'https://github.com/asmaessafi', label: 'GitHub' },
    { icon: Linkedin, href: 'https://linkedin.com/in/asma-essafi-a60a43331/', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://www.instagram.com/369asma137/', label: 'Instagram' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <footer className="relative bg-gradient-to-br from-sky-950 via-blue-950 to-indigo-950 overflow-hidden">
      {/* Subtle animated background overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(14,165,233,0.3),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(99,102,241,0.25),transparent_50%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16"
        >
          {/* Brand & Tagline */}
          <motion.div variants={itemVariants} className="space-y-6">
            <Link to="/" className="group inline-flex items-center space-x-3 cursor-pointer">
              <div className="relative">
                <span className="text-3xl font-extrabold bg-gradient-to-r from-sky-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                  {t('nav.brand')}
                </span>
                <span className="absolute -bottom-1 left-0 w-0 h-1 bg-gradient-to-r from-sky-400 to-blue-500 rounded-full transition-all duration-500 group-hover:w-full" />
              </div>
            </Link>
            <p className="text-sky-200/80 text-base leading-relaxed max-w-sm">
              {t('footer.tagline')}
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-bold text-white mb-6 relative inline-block">
              {t('footer.quickLinks')}
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-sky-500/60 rounded-full" />
            </h3>
            <ul className="space-y-4">
              {['home', 'about', 'projects', 'skills', 'contact'].map((key) => (
                <motion.li
                  key={key}
                  whileHover={{ x: 8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                >
                  <Link
                    to={key === 'home' ? '/' : `/${key}`}
                    className="text-sky-300/90 hover:text-sky-100 text-lg transition-all duration-300 flex items-center group cursor-pointer"
                  >
                    <span className="w-0 h-px bg-sky-400 transition-all duration-500 group-hover:w-8 mr-3" />
                    {t(`footer.${key}`)}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact & Social */}
          <motion.div variants={itemVariants} className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-6 relative inline-block">
                {t('footer.getInTouch')}
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-indigo-500/60 rounded-full" />
              </h3>
              <ul className="space-y-5">
                <motion.li whileHover={{ scale: 1.03 }}>
                  <a
                    href="mailto:asmaessafi137@gmail.com"
                    className="flex items-center space-x-4 text-sky-200/90 hover:text-white transition-all duration-300 group cursor-pointer"
                  >
                    <div className="p-3 bg-sky-500/20 rounded-xl group-hover:bg-sky-500/40 transition-colors">
                      <Mail size={20} className="text-sky-400" />
                    </div>
                    <span className="text-lg">asmaessafi137@gmail.com</span>
                  </a>
                </motion.li>

                <motion.li whileHover={{ scale: 1.03 }}>
                  <a
                    href="https://flowcv.com/resume/1j37uwhopbdj"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-4 text-sky-200/90 hover:text-white transition-all duration-300 group cursor-pointer"
                  >
                    <div className="p-3 bg-indigo-500/20 rounded-xl group-hover:bg-indigo-500/40 transition-colors"
                    >
                      <ExternalLink size={20} className="text-indigo-400" />
                    </div>
                    <span className="text-lg">{t('footer.viewResume')}</span>
                  </a>
                </motion.li>
              </ul>
            </div>

            {/* Social Icons */}
            <div>
              <div className="flex space-x-5">
                {socialLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <motion.a
                      key={index}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.label}
                      whileHover={{ y: -8, scale: 1.15 }}
                      whileTap={{ scale: 0.95 }}
                      className="group relative p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-sky-400/50 transition-all duration-500 cursor-pointer"
                    >
                      <Icon size={24} className="text-sky-300 group-hover:text-sky-100 transition-colors" />
                      <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-sky-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 pt-10 border-t border-white/10 flex flex-col md:flex-row justify-between items-center text-sm"
        >
          <p className="text-sky-300/70 mb-4 md:mb-0">
            &copy; {currentYear} Asma Essafi. {t('footer.copyright')}
          </p>
          <div className="flex space-x-8">
            <Link
              to="/privacy"
              className="text-sky-300/70 hover:text-sky-100 transition-colors duration-300 relative group cursor-pointer"
            >
              {t('footer.privacy')}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-sky-400 transition-all duration-300 group-hover:w-full" />
            </Link>
            <Link
              to="/terms"
              className="text-sky-300/70 hover:text-sky-100 transition-colors duration-300 relative group cursor-pointer"
            >
              {t('footer.terms')}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-sky-400 transition-all duration-300 group-hover:w-full" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Floating glow orbs for extra depth */}
      <div className="pointer-events-none absolute top-20 left-10 w-72 h-72 bg-sky-500 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse" />
      <div className="pointer-events-none absolute bottom-10 right-20 w-96 h-96 bg-indigo-600 rounded-full mix-blend-screen filter blur-3xl opacity-15 animate-pulse delay-1000" />

      {/* Tailwind Purge Hack: Hidden div to force inclusion of group-hover classes */}
      <div className="hidden group-hover:opacity-100 group-hover:text-sky-100 hover:border-sky-400/50 group-hover:bg-sky-500/40 group-hover:bg-indigo-500/40 group-hover:w-full group-hover:w-8 hover:text-white group-hover:text-white group-hover:text-sky-100 transition-all duration-300" />
    </footer>
  );
};

export default Footer;